// Ход игры от запуска до игрового поля.
//
// Порядок экранов задан таблицей переходов: автомат не даёт перескочить состояние.
// На экране всегда ровно одна сцена, её держит SceneHost.

import type { Application } from 'pixi.js';

import { fetchGameConfig, type GameConfig } from '../api/gameConfig';
import { openSession, type SessionSnapshot } from '../api/session';
import { BOOT_BUNDLE, GAME_BUNDLE, LOGO_SKELETON, PRELOADER_SHEET } from '../assets/catalog';
import { AssetManager } from '../assets/manager';
import { pickLocale, writeLocale, type Locale } from '../i18n/locale';
import { loadTranslation, useTranslation, type Translation } from '../i18n/text';
import { GameScene } from '../scenes/gameScene';
import { IntroScene } from '../scenes/introScene';
import { LoadingScene } from '../scenes/loadingScene';
import { HowlerEngine } from '../sound/howlerEngine';
import { browserMemory, GameSound, music, useSound } from '../sound/sound';
import { SceneHost } from '../ui/scene';

import { StateMachine, type Transitions } from './stateMachine';
import { SymbolLibrary } from './symbols';

/**
 * Состояние игры.
 *
 * boot — грузятся кадры экрана загрузки, на экране пусто.
 * loading — экран загрузки с прогрессом, идёт загрузка остальных ассетов и описания игры.
 * intro — экран интро, игра ждёт клик игрока: клик разблокирует звук браузера.
 * idle — игровое поле, барабаны стоят и ждут спин.
 * spinning — барабаны крутятся, игра ждёт итог раунда с бэкенда.
 * result — барабаны доезжают до итога, играют анимации выигрыша и всплывает сумма.
 */
export type GameState = 'boot' | 'loading' | 'intro' | 'idle' | 'spinning' | 'result';

/**
 * Разрешённые переходы.
 *
 * Загрузка идёт по цепочке в одну сторону, назад пути нет. Дальше игра ходит по кругу:
 * ожидание, прокрутка, показ итога и снова ожидание.
 *
 * Показ итога начинается с доводки барабанов, и на ней кнопка ещё скип: нажатие роняет их
 * и спин не тратит. Кнопка спина возвращается, когда барабаны встали, и её нажатие обрывает
 * показ, возвращает игру в ожидание и тут же начинает следующий раунд, см. game/spinRound.ts.
 *
 * Показ итога уходит не только в ожидание: за ним может идти бесплатный респин, и тогда игра
 * возвращается на прокрутку, не заходя в ожидание. Раунд от этого не кончается, см. Nudge
 * Respin в GDD, раздел 4.3.
 *
 * Выигрышные линии крутятся по кругу уже в ожидании: держать ради них игру незачем.
 */
export const TRANSITIONS: Transitions<GameState> = {
  boot: ['loading'],
  loading: ['intro'],
  intro: ['idle'],
  idle: ['spinning'],
  spinning: ['result'],
  result: ['idle', 'spinning'],
};

/** Доля прогресса, которая приходится на ассеты. Остальное — запросы. */
const ASSETS_SHARE: number = 0.9;

/** Сколько запросов идёт вместе с ассетами: описание игры, сессия и перевод. */
const REQUESTS: number = 3;

/** Сколько экран загрузки держится на виду, даже если всё уже загружено. */
const MIN_LOADING_MS: number = 1500;

/** Шаг, с которым выдержка сообщает прогресс. */
const HOLD_STEP_MS: number = 50;

/** Ждёт заданное время и сообщает долю прошедшего от 0 до 1. */
function hold(ms: number, onProgress: (progress: number) => void): Promise<void> {
  return new Promise<void>((resolve) => {
    const start: number = performance.now();

    const timer = setInterval(() => {
      const progress: number = Math.min((performance.now() - start) / ms, 1);
      onProgress(progress);

      if (progress < 1)
        return;

      clearInterval(timer);
      resolve();
    }, HOLD_STEP_MS);
  });
}

/** Всё, без чего игровое поле не собрать. */
interface LoadedGame {
  readonly config: GameConfig;
  readonly session: SessionSnapshot;
  readonly translation: Translation;
}

/**
 * Грузит ассеты игрового поля, описание игры, сессию и перевод, сообщая долю загруженного
 * от 0 до 1.
 *
 * Долю ассетов считает менеджер, запросы — по шагу на каждый. Третья задача — выдержка:
 * без неё экран загрузки на быстрой сети мелькает и пропадает.
 *
 * Полоса показывает меньшую из двух долей — загруженного и выдержанного. Поэтому она
 * не убегает вперёд таймера, а на быстрой загрузке идёт ровно, а не одним прыжком.
 */
async function loadGame(assets: AssetManager, locale: Locale, onProgress: (progress: number) => void): Promise<LoadedGame> {
  let assetsProgress = 0;
  let requestsProgress = 0;
  let holdProgress = 0;

  const report = (): void => {
    const loaded: number = assetsProgress * ASSETS_SHARE + (requestsProgress / REQUESTS) * (1 - ASSETS_SHARE);
    onProgress(Math.min(loaded, holdProgress));
  };

  const [config, session, translation]: [GameConfig, SessionSnapshot, Translation, void, void] = await Promise.all([
    fetchGameConfig().then((loaded: GameConfig) => {
      requestsProgress += 1;
      report();
      return loaded;
    }),
    openSession().then((opened: SessionSnapshot) => {
      requestsProgress += 1;
      report();
      return opened;
    }),
    loadTranslation(locale).then((loaded: Translation) => {
      requestsProgress += 1;
      report();
      return loaded;
    }),
    assets.load(GAME_BUNDLE, (progress: number) => {
      assetsProgress = progress;
      report();
    }),
    hold(MIN_LOADING_MS, (progress: number) => {
      holdProgress = progress;
      report();
    }),
  ]);

  return { config, session, translation };
}

/** Ведёт игру по состояниям: загрузка, интро, игровое поле. */
export async function startGame(app: Application): Promise<void> {
  const scenes = new SceneHost(app);

  // Звук грузится набором ассетов наравне с картинками, поэтому движок заводится тут же
  // и уходит в менеджер. Играть он начинает после жеста игрока, см. ADR-47.
  const engine = new HowlerEngine();
  const assets = new AssetManager(engine);
  const machine = new StateMachine<GameState>('boot', TRANSITIONS);

  machine.onChange((state: GameState) => {
    console.info(`Состояние игры: ${state}`);
  });

  // boot: без кадров экрана загрузки прогресс показывать не на чем.
  await assets.load(BOOT_BUNDLE);

  const loading = new LoadingScene(assets.sheet(PRELOADER_SHEET), assets.spine(LOGO_SKELETON));
  machine.goTo('loading');
  scenes.show(loading);

  // Язык выбирается до загрузки: за паком перевода игра идёт вместе с остальными запросами.
  const locale: Locale = pickLocale();

  const { config, session, translation }: LoadedGame = await loadGame(assets, locale, (progress: number) => {
    loading.setProgress(progress);
  });

  // Перевод ставится до первой надписи: экран интро уже спрашивает его о своих.
  useTranslation(translation);

  // Звук ставится до первой кнопки: кнопка звука спрашивает его о мьюте, а прочие кнопки
  // зовут его на нажатии. Мьют он берёт из памяти браузера — игрок, выключивший звук
  // в прошлый заход, не должен выключать его снова.
  const sound = new GameSound(engine, browserMemory());
  useSound(sound);

  // Ассеты символов сверяются с описанием игры здесь: незнакомый символ виден сразу.
  const symbols = new SymbolLibrary(assets, config);
  console.info(`Язык игры: ${locale}`);
  console.info(`Описание игры: символов ${config.symbols.length}, барабанов ${config.reels.length}, линий ${config.paylines.length}`);
  console.info(`Сессия ${session.id}: стадия Steam Spin ${session.state.steamSpin?.stage ?? 'нет фичи'}`);

  const intro = new IntroScene(assets);
  machine.goTo('intro');
  scenes.show(intro);

  await intro.tapped();

  // Жест игрока сделан, и браузер пускает звук. С ним заводится музыка: она тянется весь
  // ход игры, а на бонусе её сменяет своя, см. ADR-47.
  sound.unlock();
  music('main');

  machine.goTo('idle');

  let changingLocale = false;
  scenes.show(new GameScene(config, symbols, assets, machine, session, (next: Locale) => {
    if (changingLocale)
      return;

    changingLocale = true;
    void loadTranslation(next).then((loaded: Translation) => {
      writeLocale(next);
      useTranslation(loaded);
      console.info(`Язык игры: ${next}`);
    }).catch((error: unknown) => {
      console.error('Смена языка не удалась', error);
    }).finally(() => {
      changingLocale = false;
    });
  }));
}
